export const normalizeText = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Banded Levenshtein distance, returns maxDist+1 when cost would exceed maxDist */
const boundedEditDistance = (a: string, b: string, maxDist: number): number => {
  if (Math.abs(a.length - b.length) > maxDist) return maxDist + 1;
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  const curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > maxDist) return maxDist + 1;
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
};

const isSubsequence = (needle: string, haystack: string): boolean => {
  let ni = 0;
  for (let hi = 0; hi < haystack.length && ni < needle.length; hi++) {
    if (needle[ni] === haystack[hi]) ni++;
  }
  return ni === needle.length;
};

const tokenMatches = (queryToken: string, textTokens: string[]): boolean => {
  const qLen = queryToken.length;
  for (const textToken of textTokens) {
    // Substring match (also handles exact and prefix)
    if (textToken.includes(queryToken)) return true;

    // Typo tolerance: edit distance on token or prefix of matching length
    const maxDist = qLen >= 8 ? 2 : qLen >= 4 ? 1 : 0;
    if (maxDist > 0) {
      const compareLen = qLen + maxDist;
      const slice =
        textToken.length > compareLen
          ? textToken.slice(0, compareLen)
          : textToken;
      if (boundedEditDistance(queryToken, slice, maxDist) <= maxDist)
        return true;
    }

    // Subsequence for longer tokens (handles dropped letters in blurry names)
    if (qLen >= 6 && isSubsequence(queryToken, textToken)) return true;
  }
  return false;
};

/**
 * Returns true if every token in `query` matches somewhere in `text`.
 * Case-insensitive, tolerant of typos for tokens ≥4 chars.
 * Returns false when query is empty.
 */
export const fuzzyMatch = (query: string, text: string): boolean => {
  const queryTokens = normalizeText(query).split(" ").filter(Boolean);
  if (queryTokens.length === 0) return false;
  const textTokens = normalizeText(text).split(" ").filter(Boolean);
  return queryTokens.every((qt) => tokenMatches(qt, textTokens));
};
